import {
  Add,
  Close,
  FileDownloadOutlined,
  Save,
  Settings,
} from "@mui/icons-material";
import { Box, Button, IconButton, Menu, MenuItem } from "@mui/material";
import { App, Plugin, Route, View } from "@pimo/pimo-app-builder";
import {
  ChipCell,
  DateCell,
  DragAndDropLayout,
  DragDropLayoutTabDefinition,
  generateTableHeaderEntriesWithSorting,
  PMButton,
  PMPopUp,
  PMTableColumnActionEventPayload,
  PMTableSortEventPayload,
  ProgressBarCell,
  SortDirection,
  StatusIndicatorCell,
  TableUpdateStatusCell,
  TextCardCell,
} from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import dayjs from "dayjs";
import { COLORS, CONTRAST_TEXT_COLORS, Project } from "product-test-types";
import {
  canUserPerformOperation,
  getContrastColor,
  getCustomFieldTableColumnFieldName,
  getLegacyCustomFieldTableColumnFieldName,
} from "product-test-utils";
import { generatePath } from "react-router-dom";

import { TestAppState } from "../app";
import { SearchAndFilter } from "../components/project-filter-dialog";
import { APP_ROUTES } from "../constants";
import { buildCreateProjectFormRoute } from "../form/build-create-project-form-route";
import { downloadPdf, getRequestedPdfTabIndex } from "../helpers/download-pdf";
import { fetchOverlay } from "../helpers/fetch-overlay";
import { fetchOverview } from "../helpers/fetch-overview";
import { deleteProject } from "../helpers/fetch-project";
import {
  fetchTableConfiguration,
  saveTableConfiguration,
} from "../helpers/fetch-table-configuration";
import { createViewConfiguration } from "../helpers/fetch-view-configuration";
import {
  applyColumnOrderToHeaders,
  doesProjectPassFilterCriteria,
  OVERVIEW_TABLE_FIELD_ORDER,
} from "../helpers/pages-helpers/overview-helpers";
import {
  addProjectsTableComponent,
  type ProjectsTableColumnProps,
} from "../helpers/pages-helpers/projects-table-component";
import { showSnackbar } from "../helpers/show-snackbar";
import { theme } from "../theme";
import {
  getDropdownColorValue,
  getDropdownLabel,
} from "../utils/dropdown-options";
import { formatDate, formatDateTime } from "../utils/format-date";
import { getAddProjectButtonLabel } from "../utils/get-add-new-label";
import { getLocalizedTableColumnLabel } from "../utils/get-localized-table-column-label";
import { getLockedByMessage } from "../utils/get-locked-by-message";
import { getPluralOfProjectName } from "../utils/get-plural-of-project-name";
import { sortProjects } from "../utils/sort-table-data";
import { OVERVIEW_CARDS } from "./overview";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface OverviewPluginState {}

export class OverviewPlugin implements Plugin<TestAppState, TestAppState> {
  route?: Route;
  private view?: View<TestAppState, DragAndDropLayout>;
  private app?: App<TestAppState>;
  private downloadMenuAnchor?: HTMLElement | null;
  onRegister(app: App<TestAppState>): void {
    this.app = app;

    this.view = app.createView({
      name: "Project Overview",
      layout: new DragAndDropLayout(),
    });

    const view = this.view;
    this.route = app.createRoute({
      path: APP_ROUTES.overview,
      view: view,
    });

    this.view.mapLayoutState(
      ({
        overviewLayout,
        settings,
        userProfile,
        showDeleteProjectDialog,
        pendingDeleteProjectId,
        language,
        overviewTableConfiguration,
      }) => {
        const confirmationPopupType =
          overviewTableConfiguration?.confirmationPopupType;
        const dynamicProjectOverviewTitle = getLocalizedMessage(
          language,
          "overview",
          {
            overviewItem: getPluralOfProjectName(
              settings?.configuration?.projectName,
              language
            ),
          }
        );

        let tabs;
        if (overviewLayout?.tabs && overviewLayout.tabs.length > 0) {
          tabs = overviewLayout.tabs.map((tab, index) => ({
            ...tab,
            title: index === 0 ? dynamicProjectOverviewTitle : tab.title,
          }));
        } else {
          tabs = [
            {
              id: 0,
              title: dynamicProjectOverviewTitle,
              rows: [{ id: 0, columnCount: 3, columns: [] }],
            },
          ];
        }
        return {
          canEdit: canUserPerformOperation({
            userGroups: userProfile?.groups || [],
            lowestLevelAccessRequiredToPerformThisOperation: "admin",
            client: settings?.configuration?.client,
            tool: settings?.configuration?.tool,
          }),
          isComponentEditingEnabled: true,

          displayTabs: overviewLayout?.displayTabs ?? true,
          activeTabIndex: getRequestedPdfTabIndex(),
          language: language || "en",
          tabs,
          rightSlot: (
            <>
              <Box
                className="no-print"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <PMButton
                  size="medium"
                  variant="white"
                  icon={<Add />}
                  text={getAddProjectButtonLabel(language)}
                  onClick={() => {
                    this.app?.navigate(
                      generatePath(APP_ROUTES.projectDialog, {
                        id: "new-project",
                        isFromOverview: "true",
                      })
                    );
                  }}
                />
                <IconButton
                  onClick={(event) => {
                    this.downloadMenuAnchor = event.currentTarget;
                    this.app?.patchAppState({});
                  }}
                  sx={{ ml: 1 }}
                >
                  <FileDownloadOutlined />
                </IconButton>
              </Box>
              <Menu
                anchorEl={this.downloadMenuAnchor}
                open={Boolean(this.downloadMenuAnchor)}
                onClose={() => {
                  this.downloadMenuAnchor = null;
                  this.app?.patchAppState({});
                }}
              >
                <MenuItem
                  onClick={() => {
                    this.downloadMenuAnchor = null;
                    this.app?.patchAppState({});
                    void downloadPdf().catch((error: unknown) => {
                      console.error("PDF export failed", error);
                      if (this.app) {
                        showSnackbar(
                          this.app as never,
                          getLocalizedMessage(
                            language,
                            "generic.internalError"
                          ) || "Could not generate the PDF.",
                          "error"
                        );
                      }
                    });
                  }}
                >
                  {getLocalizedMessage(language, "download.pdf") ||
                    "Download PDF"}
                </MenuItem>
              </Menu>
              <PMPopUp
                open={!!showDeleteProjectDialog}
                title={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.title.confirmDelete"
                  ) || "Confirm Delete"
                }
                description={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.message.genericConfirm",
                    { itemName: settings?.configuration?.projectName ?? "" }
                  ) ||
                  `Are you sure you want to delete this ${settings?.configuration?.projectName}?`
                }
                onClose={() => {
                  this.app?.patchAppState({
                    showDeleteProjectDialog: false,
                    pendingDeleteProjectId: undefined,
                  });
                }}
                maxWidth="sm"
                cancelButtonText={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.button.cancel"
                  ) || "Cancel"
                }
                cancelButtonFireEvent={() => {
                  this.app?.patchAppState({
                    showDeleteProjectDialog: false,
                    pendingDeleteProjectId: undefined,
                  });
                }}
                cancelButtonColor="white"
                confirmationButtonColor="error"
                confirmationButtonText={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.button.delete"
                  ) || "Delete"
                }
                confirmationButtonFireEvent={async () => {
                  if (!this.app) return;
                  const projectId = pendingDeleteProjectId;
                  if (projectId) {
                    try {
                      await deleteProject(projectId);
                      this.app.patchAppState({
                        overlay: await fetchOverlay(),
                        overview: await fetchOverview(),
                      });
                    } catch {
                      // ignore errors for now
                    }
                  }

                  this.app.patchAppState({
                    showDeleteProjectDialog: false,
                    pendingDeleteProjectId: undefined,
                  });
                }}
              />
              {/* Confirmation dialog for saving overview layout changes across overview pages */}
              <PMPopUp
                open={
                  !!(
                    this.app &&
                    this.app.getAppState().showConfirmSaveOverviewLayoutDialog
                  )
                }
                title={
                  getLocalizedMessage(
                    language || "en",
                    "saveLayoutDialog.title"
                  ) || "Save layout changes?"
                }
                description={
                  getLocalizedMessage(
                    language || "en",
                    "saveLayoutDialog.message"
                  ) || "Do you want to save the layout changes?"
                }
                onClose={() => {
                  this.app?.patchAppState({
                    showConfirmSaveOverviewLayoutDialog: false,
                  });
                }}
                maxWidth="sm"
                cancelButtonText={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.button.cancel"
                  ) || "Cancel"
                }
                cancelButtonFireEvent={() => {
                  this.app?.patchAppState({
                    showConfirmSaveOverviewLayoutDialog: false,
                  });
                }}
                cancelButtonColor="white"
                confirmationButtonColor="blue"
                confirmationButtonText={
                  getLocalizedMessage(language, "dialog.button.save") || "Save"
                }
                confirmationButtonFireEvent={async () => {
                  if (!this.app) return;

                  const { pendingSaveOverviewLayout } = this.app.getAppState();

                  // clear dialog flag
                  this.app.patchAppState({
                    showConfirmSaveOverviewLayoutDialog: false,
                  });

                  if (pendingSaveOverviewLayout) {
                    try {
                      const { overviewLayout } = this.app.getAppState();
                      await createViewConfiguration({
                        tabs: pendingSaveOverviewLayout.tabs,
                        canEdit: overviewLayout?.canEdit ?? true,
                        displayTabs: pendingSaveOverviewLayout.displayTabs,
                        type: pendingSaveOverviewLayout.type ?? "overview",
                      });
                      // success snackbar
                      this.app.patchAppState({
                        snackbar: {
                          message:
                            getLocalizedMessage(
                              language || "en",
                              "layoutSavedSuccess"
                            ) ||
                            "Layout saved. Changes have been applied to all overview pages.",
                          severity: "success",
                          open: true,
                          type: "snackbar",
                        },
                      });
                    } catch {
                      // error snackbar
                      this.app.patchAppState({
                        snackbar: {
                          message:
                            getLocalizedMessage(
                              language || "en",
                              "layoutSaveFailedOverview"
                            ) ||
                            "Failed to save overview layout changes. Please try again.",
                          severity: "error",
                          open: true,
                          type: "snackbar",
                        },
                      });
                    }
                  } else {
                    // if not confirmed, revert the in-memory layout to the last saved layout
                    const { overviewLayout } = this.app.getAppState();
                    if (overviewLayout && overviewLayout.tabs) {
                      this.app.patchAppState({
                        overviewLayout: overviewLayout,
                      });
                    }
                  }

                  // remove pending data
                  this.app.patchAppState({
                    pendingSaveOverviewLayout: undefined,
                  });
                }}
              />
              <PMPopUp
                open={!!confirmationPopupType}
                title={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.title.unsavedChanges"
                  ) || "Unsaved Changes"
                }
                description={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.message.abortTableConfiguration"
                  ) ||
                  "You have made changes that have not been saved. If you close the table configurator, these changes will be lost."
                }
                onClose={async () => {
                  const tableConfiguration =
                    this.app?.getAppState().overviewTableConfiguration;

                  this.app?.patchAppState({
                    overviewTableConfiguration: {
                      ...tableConfiguration,
                      tableHeaderEntries:
                        tableConfiguration?.tableHeaderEntries ?? [],
                      isEditMode: true,
                      confirmationPopupType: undefined,
                    },
                  });
                }}
                maxWidth="sm"
                cancelButtonText={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.button.cancel"
                  ) || "Cancel"
                }
                cancelButtonFireEvent={async () => {
                  const tableConfiguration =
                    this.app?.getAppState().overviewTableConfiguration;

                  this.app?.patchAppState({
                    overviewTableConfiguration: {
                      ...tableConfiguration,
                      tableHeaderEntries:
                        tableConfiguration?.tableHeaderEntries ?? [],
                      isEditMode: true,
                      confirmationPopupType: undefined,
                    },
                  });
                }}
                cancelButtonColor="white"
                confirmationButtonColor="blue"
                confirmationButtonText={
                  confirmationPopupType === "save"
                    ? getLocalizedMessage(
                        language || "en",
                        "dialog.button.save"
                      ) || "Save"
                    : getLocalizedMessage(
                        language || "en",
                        "dialog.button.continue"
                      ) || "Continue"
                }
                confirmationButtonFireEvent={async () => {
                  if (!this.app) return;

                  const savedConfig = await fetchTableConfiguration(
                    "projectsTableComponent"
                  );

                  this.app?.patchAppState({
                    overviewTableConfiguration: {
                      isEditMode: false,
                      tableHeaderEntries: savedConfig?.columns ?? [],
                    },
                    overviewPluginColumnOrder: undefined,
                  });
                }}
              />
            </>
          ),
        };
      }
    );
    buildCreateProjectFormRoute(app, this.route, true);
    this.view.on("updateLayout", async ({ payload }) => {
      if (!payload || !this.app) return;

      const { overviewLayout } = this.app.getAppState();

      const currentDisplayTabs = overviewLayout?.displayTabs ?? true;

      const { tabs } = payload as { tabs: DragDropLayoutTabDefinition[] };

      // apply in-memory so UI updates immediately
      this.app.patchAppState({
        overviewLayout: {
          displayTabs: currentDisplayTabs,
          tabs,
        },
      });

      // open confirmation dialog and store pending layout — defer persisting until confirmed
      this.app.patchAppState({
        pendingSaveOverviewLayout: {
          tabs,
          displayTabs: currentDisplayTabs,
          type: "overview",
        },
        showConfirmSaveOverviewLayoutDialog: true,
      });
    });
    this.view?.on("startLayoutEdit", () => {
      this.app?.patchAppState({
        isSidebarOpen: false,
      });
    });

    this.view?.on("stopLayoutEdit", () => {
      this.app?.patchAppState({
        isSidebarOpen: true,
      });
    });

    this.view.buildDynamicallyBasedOnAppState(
      (appState) => ({
        configuration: appState.settings?.configuration,
        project: appState.currentReport?.project,
      }),

      (state, view) => {
        // static components are built here
        this.buildOverview();

        const { projectsTableComponent } = addProjectsTableComponent({
          view,
          configuration: state.configuration,
        });

        const getProjectsTableColumnLabel = (
          currentLanguage: string,
          fieldName: string,
          categoryLabel?: string | undefined
        ) =>
          getLocalizedTableColumnLabel(
            currentLanguage,
            fieldName,
            {},
            categoryLabel
          );

        projectsTableComponent.on("sort", (event) => {
          if (!event?.payload || !this.app) {
            return;
          }

          const payload = event?.payload as PMTableSortEventPayload<
            keyof Project
          >;
          const state = this.app.getAppState();
          let direction: SortDirection = "ASC";

          // Swap direction if key is already being sorted by in ascending order.
          if (
            state.overviewPluginSortData?.key === payload?.sort?.key &&
            state.overviewPluginSortData?.direction === "ASC"
          ) {
            direction = "DESC";
          }

          this.app.patchAppState({
            overviewPluginSortData: {
              key: payload?.sort?.key,
              direction,
            },
          });
        });

        projectsTableComponent.mapState(
          ({
            overview,
            settings,
            overviewPluginSortData,
            overviewPluginColumnOrder,
            userProfile,
            language,
            isSidebarOpen,
            filterData,
            overviewTableConfiguration,
            lockDetails,
          }) => {
            const resourceLockPlugin =
              this.app?.getPluginByName<ResourceLockPlugin<TestAppState>>(
                "ResourceLockPlugin"
              );

            const lockedProjects = lockDetails?.projectLocks;
            const projectItems = sortProjects(
              overview?.projects ?? [],
              overviewPluginSortData
            );
            const isOwnerEnabled =
              settings?.configuration?.fieldsDefinition
                ?.filter((field) => field.purpose === "projectBasic")
                ?.find((field) => field.fieldName === "owner")?.enabled ??
              false;
            const isProgressEnabled = settings?.configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemReporting")
              ?.find((field) => field.fieldName === "progress")?.enabled;

            const isStatusEnabled =
              settings?.configuration?.fieldsDefinition
                ?.filter((field) => field.purpose === "itemReporting")
                ?.find((field) => field.fieldName === "status")?.enabled ??
              false;

            const isDegreeOfCompletionEnabled =
              settings?.configuration?.fieldsDefinition
                ?.filter((field) => field.purpose === "itemReporting")
                ?.find((field) => field.fieldName === "completion")?.enabled ??
              false;

            const isCategoryEnabled =
              settings?.configuration?.fieldsDefinition
                ?.filter((field) => field.purpose === "projectBasic")
                ?.find((field) => field.fieldName === "category")?.enabled ??
              false;

            const isStartDateEnabled =
              settings?.configuration?.fieldsDefinition
                ?.filter((field) => field.purpose === "projectBasic")
                ?.find((field) => field.fieldName === "startDate")?.enabled ??
              false;

            const isEndDateEnabled =
              settings?.configuration?.fieldsDefinition
                ?.filter((field) => field.purpose === "projectBasic")
                ?.find((field) => field.fieldName === "endDate")?.enabled ??
              false;

            const rawTableHeaderEntries =
              overviewTableConfiguration?.tableHeaderEntries ?? [];

            const enabledProjectCustomFields =
              settings?.configuration?.customFields?.filter(
                (field) =>
                  field?.enabled &&
                  field?.flag !== "delete" &&
                  (field?.purpose === "projectBasic" ||
                    field?.purpose === "projectReporting")
              ) ?? [];

            const customLabelByFieldName = new Map(
              enabledProjectCustomFields.flatMap((field) => [
                [getCustomFieldTableColumnFieldName(field), field.label],
                [
                  getLegacyCustomFieldTableColumnFieldName(field.label),
                  field.label,
                ],
              ])
            );

            const customFieldDefinitionByFieldName = new Map(
              enabledProjectCustomFields.flatMap((field) => [
                [getCustomFieldTableColumnFieldName(field), field],
                [getLegacyCustomFieldTableColumnFieldName(field.label), field],
              ])
            );

            const orderedUniqueHeaders = rawTableHeaderEntries.filter(
              (entry, index, source) =>
                source.findIndex((e) => e.fieldName === entry.fieldName) ===
                index
            );

            const orderedUniqueKnownHeaders = orderedUniqueHeaders.filter(
              (entry) =>
                OVERVIEW_TABLE_FIELD_ORDER.includes(
                  entry.fieldName as (typeof OVERVIEW_TABLE_FIELD_ORDER)[number]
                )
            );

            const orderedUniqueCustomHeaders = orderedUniqueHeaders
              .filter(
                (entry) =>
                  String(entry.fieldName).startsWith("customField-") &&
                  customFieldDefinitionByFieldName.has(String(entry.fieldName))
              )
              .map((entry) => {
                const customFieldDefinition =
                  customFieldDefinitionByFieldName.get(String(entry.fieldName));

                return {
                  ...entry,
                  fieldName: customFieldDefinition
                    ? getCustomFieldTableColumnFieldName(customFieldDefinition)
                    : entry.fieldName,
                  label:
                    customLabelByFieldName.get(String(entry.fieldName)) ??
                    entry.label,
                };
              });

            const existingCustomFieldNames = new Set(
              orderedUniqueCustomHeaders.map((entry) => String(entry.fieldName))
            );

            const missingEnabledCustomHeaders = enabledProjectCustomFields
              .map((field) => {
                const fieldName = getCustomFieldTableColumnFieldName(field);
                if (existingCustomFieldNames.has(fieldName)) {
                  return null;
                }

                return {
                  fieldName,
                  label: field.label,
                  isSortable: true,
                  isHidden: false,
                  sortDirection: null,
                };
              })
              .filter(
                (entry): entry is NonNullable<typeof entry> => entry != null
              );

            const headerByField = new Map(
              orderedUniqueKnownHeaders.map((entry) => [entry.fieldName, entry])
            );

            // Ensure one header per canonical column so header/body indices always align.
            const normalizedBaseHeaders = OVERVIEW_TABLE_FIELD_ORDER.map(
              (fieldName) => {
                const fromConfig = headerByField.get(fieldName);

                return {
                  ...(fromConfig ?? {
                    fieldName,
                    isSortable: true,
                    isHidden: false,
                    sortDirection: null,
                  }),
                  fieldName,
                  label: getProjectsTableColumnLabel(
                    language,
                    fieldName,
                    fromConfig?.label
                  ),
                };
              }
            );

            const normalizedHeaders = [
              ...normalizedBaseHeaders,
              ...orderedUniqueCustomHeaders,
              ...missingEnabledCustomHeaders,
            ];

            const normalizedHeaderByFieldName = new Map(
              normalizedHeaders.map((entry) => [String(entry.fieldName), entry])
            );

            // Saved table configuration order is the source of truth.
            // Keep that order, then append any missing columns.
            const savedOrderedFieldNames = orderedUniqueHeaders
              .map((entry) => {
                const rawFieldName = String(entry.fieldName);
                const customFieldDefinition =
                  customFieldDefinitionByFieldName.get(rawFieldName);

                return customFieldDefinition
                  ? getCustomFieldTableColumnFieldName(customFieldDefinition)
                  : rawFieldName;
              })
              .filter(
                (fieldName, index, source) =>
                  source.findIndex((value) => value === fieldName) === index
              )
              .filter((fieldName) =>
                normalizedHeaderByFieldName.has(fieldName)
              );

            const missingFieldNames = normalizedHeaders
              .map((entry) => String(entry.fieldName))
              .filter(
                (fieldName) => !savedOrderedFieldNames.includes(fieldName)
              );

            const orderedHeadersForRender = [
              ...savedOrderedFieldNames,
              ...missingFieldNames,
            ].map((fieldName) => normalizedHeaderByFieldName.get(fieldName)!);

            const tableHeaderEntries = generateTableHeaderEntriesWithSorting<
              Project & {
                progress: number;
                severity: string;
                status: string;
                degreeOfCompletion: string;
                endDate: string;
                updateStatus: string;
                rating: number;
              }
            >({
              entries: orderedHeadersForRender.map((e) => ({
                ...e,
                fieldName: e.fieldName as keyof (Project & {
                  progress: number;
                  severity: string;
                  status: string;
                  degreeOfCompletion: string;
                  endDate: string;
                  updateStatus: string;
                  rating: number;
                }),
                label:
                  e.label || getLocalizedMessage(language, `${e.fieldName}`),
              })),
              tableSortData: overviewPluginSortData,
            });

            const headerVisibilityByField: Record<string, boolean> = {
              customId: true,
              name: true,
              owner: isOwnerEnabled,
              category: isCategoryEnabled,
              status: isStatusEnabled,
              degreeOfCompletion: isDegreeOfCompletionEnabled,
              progress: !!isProgressEnabled,
              startDate: isStartDateEnabled,
              endDate: isEndDateEnabled,
              updateStatus: true,
            };

            const tableHeaderEntriesWithVisibility = tableHeaderEntries.map(
              (entry) => {
                const isIdColumn = entry.fieldName === "customId";
                const isCustomFieldColumn = String(entry.fieldName).startsWith(
                  "customField-"
                );
                const sxObject =
                  entry.sx &&
                  typeof entry.sx === "object" &&
                  !Array.isArray(entry.sx)
                    ? (entry.sx as Record<string, unknown>)
                    : undefined;

                const baseSx = isIdColumn
                  ? {
                      ...(sxObject ?? {}),
                      position: "sticky" as const,
                      left: 0,
                      zIndex: 4,
                      backgroundColor: "background.paper",
                    }
                  : sxObject
                    ? (() => {
                        const sxWithoutSticky = { ...sxObject };
                        delete sxWithoutSticky.position;
                        delete sxWithoutSticky.left;
                        delete sxWithoutSticky.right;
                        return sxWithoutSticky;
                      })()
                    : entry.sx;

                const isCategoryColumn = String(entry.fieldName) === "category";

                const normalizedSx =
                  isCustomFieldColumn &&
                  baseSx &&
                  typeof baseSx === "object" &&
                  !Array.isArray(baseSx)
                    ? {
                        ...baseSx,
                        minWidth: 200,
                        width: 200,
                        maxWidth: 200,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                      }
                    : isCustomFieldColumn
                      ? {
                          minWidth: 200,
                          width: 200,
                          maxWidth: 200,
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                        }
                      : isCategoryColumn
                        ? {
                            ...(baseSx &&
                            typeof baseSx === "object" &&
                            !Array.isArray(baseSx)
                              ? (baseSx as Record<string, unknown>)
                              : {}),
                            width: "200px",
                            minWidth: "200px",
                          }
                        : baseSx;

                return {
                  ...entry,
                  sx: normalizedSx,
                  isHidden:
                    entry?.isHidden ||
                    headerVisibilityByField[entry.fieldName] === false,
                };
              }
            );

            const defaultColumnOrder = tableHeaderEntriesWithVisibility.map(
              (_, index) => index
            );

            const isValidColumnOrder = (order: number[] | undefined) => {
              if (
                !order ||
                order.length !== tableHeaderEntriesWithVisibility.length
              ) {
                return false;
              }

              const expected = new Set(defaultColumnOrder);
              const actual = new Set(order);

              if (actual.size !== expected.size) {
                return false;
              }

              return [...expected].every((index) => actual.has(index));
            };

            const effectiveColumnOrder = isValidColumnOrder(
              overviewPluginColumnOrder
            )
              ? overviewPluginColumnOrder
              : defaultColumnOrder;

            // Seed order once after refresh so drag/reorder state is restored.
            if (!isValidColumnOrder(overviewPluginColumnOrder)) {
              this.app?.patchAppState({
                overviewPluginColumnOrder: effectiveColumnOrder,
              });
            }

            return {
              title: getLocalizedMessage(language, "overview", {
                overviewItem: getPluralOfProjectName(
                  settings?.configuration?.projectName,
                  language
                ),
              }),
              isSidebarOpen: isSidebarOpen,
              rightSlotOverride: (
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  <PMButton
                    size="medium"
                    variant="whiteOutlined"
                    icon={<Add />}
                    text={getAddProjectButtonLabel(language)}
                    onClick={() => {
                      this.app?.navigate(
                        generatePath(APP_ROUTES.projectDialog, {
                          id: "new-project",
                          isFromOverview: "true",
                        })
                      );
                    }}
                  />

                  <SearchAndFilter
                    language={language || "en"}
                    filterData={filterData}
                    handleChange={(newFilterData) => {
                      this.app?.patchAppState({
                        filterData: newFilterData,
                      });
                    }}
                    clearAllFilters={() => {
                      this.app?.patchAppState({
                        filterData: undefined,
                      });
                    }}
                    options={{
                      status: {
                        isEnabled: isStatusEnabled,
                        options:
                          settings?.configuration?.statusDefinition?.map(
                            (statusDef) => statusDef.name
                          ) || [],
                      },
                      completion: {
                        isEnabled: isDegreeOfCompletionEnabled,
                        options:
                          settings?.configuration?.degreeOfCompletionDefinition?.map(
                            (completionDef) => completionDef.name
                          ) || [],
                      },
                      category: {
                        isEnabled: isCategoryEnabled,
                        options:
                          settings?.configuration?.categoryDefinition?.map(
                            (categoryDef) => categoryDef.name
                          ) || [],
                      },
                      updateStatus: {
                        isEnabled: true,
                        options: ["On Track", "At Risk", "Off Track"],
                      },
                    }}
                  ></SearchAndFilter>
                  {canUserPerformOperation({
                    userGroups: userProfile?.groups || [],
                    lowestLevelAccessRequiredToPerformThisOperation: "admin",
                    client: settings?.configuration?.client,
                    tool: settings?.configuration?.tool,
                  }) &&
                    (this.app?.getAppState().overviewTableConfiguration
                      ?.isEditMode ? (
                      <Box
                        sx={{
                          display: "flex",
                          p: 0,
                          gap: 0,
                        }}
                      >
                        <Button
                          aria-label={
                            getLocalizedMessage(language, "saveTableChanges") ||
                            "Save table changes"
                          }
                          sx={{
                            borderRadius: "100%",
                            width: "40px",
                            height: "40px",
                            minWidth: "40px",
                            padding: 0,
                          }}
                          title={
                            getLocalizedMessage(language, "saveTableChanges") ||
                            "Save table changes"
                          }
                          onClick={async () => {
                            // Persist headers in current display order so refresh can rebuild colOrder.
                            const orderedHeadersForSave =
                              applyColumnOrderToHeaders(
                                this.app
                                  ?.getAppState()
                                  .overviewTableConfiguration?.tableHeaderEntries.map(
                                    (entry) => ({
                                      ...entry,
                                    })
                                  ) ?? [],
                                this.app?.getAppState()
                                  .overviewPluginColumnOrder
                              );

                            await saveTableConfiguration({
                              component: "projectsTableComponent",
                              columns: orderedHeadersForSave,
                            });

                            const identityColumnOrder =
                              orderedHeadersForSave.map((_, index) => index);

                            this.app?.patchAppState({
                              overviewTableConfiguration: {
                                isEditMode: false,
                                tableHeaderEntries: orderedHeadersForSave,
                              },
                              overviewPluginColumnOrder: identityColumnOrder,
                            });
                          }}
                        >
                          <Save sx={{ height: "20px", width: "20px" }} />
                        </Button>
                        <Button
                          aria-label={
                            getLocalizedMessage(
                              language,
                              "closeTableConfiguration"
                            ) || "Close table configuration"
                          }
                          sx={{
                            borderRadius: "100%",
                            width: "40px",
                            height: "40px",
                            minWidth: "40px",
                            padding: 0,
                          }}
                          title={
                            getLocalizedMessage(
                              language,
                              "closeTableConfiguration"
                            ) || "Close table configuration"
                          }
                          onClick={async () => {
                            const tableConfiguration =
                              this.app?.getAppState()
                                .overviewTableConfiguration;

                            if (!tableConfiguration) {
                              return;
                            }

                            if (tableConfiguration.isDirty) {
                              // Show Confirmation Dialog
                              this.app?.patchAppState({
                                overviewTableConfiguration: {
                                  ...tableConfiguration,
                                  confirmationPopupType: "abort",
                                },
                              });

                              return;
                            }

                            // Config is not dirty, just exit edit mode
                            const savedConfig = await fetchTableConfiguration(
                              "projectsTableComponent"
                            );

                            this.app?.patchAppState({
                              overviewTableConfiguration: {
                                isEditMode: false,
                                tableHeaderEntries: savedConfig?.columns ?? [],
                              },
                              overviewPluginColumnOrder: undefined,
                            });
                          }}
                        >
                          <Close sx={{ height: "20px", width: "20px" }} />
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        aria-label={
                          getLocalizedMessage(language, "configureTable") ||
                          "Configure table"
                        }
                        sx={{
                          borderRadius: "100%",
                          width: "40px",
                          height: "40px",
                          minWidth: "40px",
                          padding: 0,
                        }}
                        title={
                          getLocalizedMessage(language, "configureTable") ||
                          "Configure table"
                        }
                        onClick={() => {
                          this?.app?.patchAppState({
                            overviewTableConfiguration: {
                              isEditMode: true,
                              tableHeaderEntries: tableHeaderEntries.map(
                                (entry) => ({
                                  ...entry,
                                })
                              ),
                            },
                          });
                        }}
                      >
                        <Settings sx={{ height: "20px", width: "20px" }} />
                      </Button>
                    ))}
                </Box>
              ),
              showInfoIcon: false,
              showMenuIcon: false,
              allowColumnReorder: true,
              isEditMode: overviewTableConfiguration?.isEditMode ?? false,
              colOrder: effectiveColumnOrder,
              setColOrder: (order) =>
                this.app?.patchAppState({ overviewPluginColumnOrder: order }),
              data: projectItems
                .filter((entry) =>
                  doesProjectPassFilterCriteria(entry, filterData, settings)
                )
                .map((entry) => {
                  // Translate the status
                  const updateStatusKey =
                    entry._calculated?.updateStatus?.status?.toLowerCase();

                  const translatedStatus =
                    (updateStatusKey
                      ? getLocalizedMessage(language, updateStatusKey)
                      : undefined) ??
                    entry._calculated?.updateStatus?.status ??
                    "";

                  const projectEditLock = lockedProjects?.[entry.id]?.edit;
                  const isEditLocked =
                    projectEditLock?.isLocked &&
                    projectEditLock?.lockedBy !== userProfile?.email;

                  const isCompleted =
                    entry._calculated?.completionDate &&
                    dayjs(entry._calculated.completionDate).isValid() &&
                    dayjs(entry._calculated.completionDate).isBefore(dayjs());
                  const isSubmitted = Boolean(entry.submitter);

                  const projectCustomFields = (
                    entry as Project & {
                      customFields?: {
                        value?: unknown;
                        customFieldDefinition?: {
                          label?: string;
                        };
                      }[];
                    }
                  ).customFields;

                  const customFieldValueByFieldName = new Map<
                    string,
                    unknown
                  >();
                  const customFieldValueByLabel = new Map<string, unknown>();

                  for (const customFieldValue of projectCustomFields ?? []) {
                    const fieldName = getCustomFieldTableColumnFieldName(
                      customFieldValue.customFieldDefinition
                    );
                    const labelKey = String(
                      customFieldValue.customFieldDefinition?.label ?? ""
                    )
                      .trim()
                      .toLowerCase();
                    const nextValue = customFieldValue.value;
                    const currentValue =
                      customFieldValueByFieldName.get(fieldName);
                    const currentLabelValue =
                      customFieldValueByLabel.get(labelKey);

                    const hasMeaningfulValue =
                      nextValue != null && String(nextValue).trim() !== "";
                    const currentHasMeaningfulValue =
                      currentValue != null &&
                      String(currentValue).trim() !== "";
                    const labelHasMeaningfulValue =
                      currentLabelValue != null &&
                      String(currentLabelValue).trim() !== "";

                    // Keep whichever row has an actual value to avoid null placeholder rows
                    // overriding real persisted values for the same custom field.
                    if (!currentHasMeaningfulValue || hasMeaningfulValue) {
                      customFieldValueByFieldName.set(fieldName, nextValue);
                    }

                    if (
                      labelKey &&
                      (!labelHasMeaningfulValue || hasMeaningfulValue)
                    ) {
                      customFieldValueByLabel.set(labelKey, nextValue);
                    }
                  }
                  const backgroundColor = getDropdownColorValue(
                    settings?.configuration?.categoryDefinition,
                    entry.category,
                    "#0B2753"
                  );
                  const contrastTextColor = getContrastColor(
                    backgroundColor,
                    theme.palette.text.primary
                  );

                  const baseCellByFieldName = new Map<
                    string,
                    Record<string, unknown>
                  >([
                    [
                      "customId",
                      {
                        component: TextCardCell,
                        body: String(entry.customId),
                        cardProps: {
                          sx: {
                            position: "sticky" as const,
                            left: 0,
                            zIndex: 3,
                            minWidth: 120,
                            width: 120,
                            backgroundColor: "background.paper",
                          },
                        },
                      },
                    ],
                    [
                      "name",
                      {
                        component: TextCardCell,
                        body: String(entry.name),
                        cutBodyAfter: 51,
                        cardProps: {
                          sx: {
                            zIndex: 3,
                            minWidth: 300,
                            width: 300,
                            backgroundColor: "background.paper",
                          },
                        },
                        tooltipProps: {
                          placement: "right",
                          slotProps: {
                            tooltip: {
                              sx: {
                                backgroundColor: "#fff",
                                color: "#414141",
                                border: "1px solid",
                                borderColor: "background.paper",
                                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                                fontSize: "14px",
                                fontWeight: 400,
                                padding: "12px 16px",
                                borderRadius: "8px",
                              },
                            },
                          },
                        },
                      },
                    ],
                    [
                      "owner",
                      {
                        component: TextCardCell,
                        body: String(entry.owner),
                        cutBodyAfter: 20,
                        tooltipProps: {
                          placement: "right",
                          slotProps: {
                            tooltip: {
                              sx: {
                                backgroundColor: "#fff",
                                color: "#414141",
                                border: "1px solid",
                                borderColor: "background.paper",
                                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                                fontSize: "14px",
                                fontWeight: 400,
                                padding: "12px 16px",
                                borderRadius: "8px",
                              },
                            },
                          },
                        },
                      },
                    ],
                    [
                      "category",
                      {
                        component: ChipCell,
                        body: getDropdownLabel(
                          settings?.configuration?.categoryDefinition,
                          entry.category
                        ),
                        chipProps: {
                          sx: {
                            backgroundColor,
                            color: contrastTextColor,
                            borderRadius: "8px",
                            px: 2,
                          },
                        },
                      },
                    ],
                    [
                      "status",
                      {
                        component: StatusIndicatorCell,
                        body:
                          settings?.configuration?.statusReportingLevel ===
                          "category"
                            ? (entry._calculated?.overallSemaphoreStatus
                                ?.name ?? "")
                            : (entry?._calculated?.overallStatus?.name ?? ""),
                        status:
                          settings?.configuration?.statusReportingLevel ===
                          "category"
                            ? (entry._calculated?.overallSemaphoreStatus
                                ?.color ?? "")
                            : getDropdownColorValue(
                                settings?.configuration?.statusDefinition,
                                entry?._calculated?.overallStatus?.name,
                                ""
                              ),
                      },
                    ],
                    [
                      "degreeOfCompletion",
                      {
                        component: StatusIndicatorCell,
                        body:
                          entry?._calculated?.overallDegreeOfCompletion?.name ??
                          "",
                        status: getDropdownColorValue(
                          settings?.configuration?.degreeOfCompletionDefinition,
                          entry?._calculated?.overallDegreeOfCompletion?.name,
                          ""
                        ),
                      },
                    ],
                    [
                      "progress",
                      {
                        component: ProgressBarCell,
                        header: `${Math.round(
                          entry?._calculated?.averageProgress ?? 0
                        )} %`,
                        progress: entry?._calculated?.averageProgress ?? 0,
                        progressColor: theme.palette.primary.contrastText,
                        progressBarHeight: "8px",
                      },
                    ],
                    [
                      "startDate",
                      {
                        component: DateCell,
                        date: entry?.startDate
                          ? formatDate(entry?.startDate)
                          : "N/A",
                      },
                    ],
                    [
                      "endDate",
                      {
                        component: DateCell,
                        date: entry?.endDate
                          ? formatDate(entry?.endDate)
                          : "N/A",
                      },
                    ],
                    [
                      "updateStatus",
                      {
                        component: TableUpdateStatusCell,
                        body: isSubmitted
                          ? `Submitted: ${
                              entry.submittedAt &&
                              dayjs(entry.submittedAt).isValid()
                                ? formatDate(entry.submittedAt, language)
                                : "N/A"
                            }`
                          : isCompleted
                            ? getLocalizedMessage(
                                language || "en",
                                "completed"
                              ) || "Completed"
                            : translatedStatus,
                        icon:
                          isCompleted || isSubmitted
                            ? undefined
                            : entry._calculated?.updateStatus?.icon,
                        muiIcon:
                          isCompleted || isSubmitted
                            ? "CheckCircleOutlined"
                            : undefined,
                        chipColor:
                          isCompleted || isSubmitted
                            ? COLORS[0]
                            : (entry._calculated?.updateStatus?.color ??
                              COLORS[0]),
                        chipProps: {
                          sx: {
                            color: isCompleted
                              ? CONTRAST_TEXT_COLORS[0]
                              : (entry._calculated?.updateStatus
                                  ?.contrastTextColor ??
                                CONTRAST_TEXT_COLORS[0]),
                          },
                        },
                        tooltip: isSubmitted
                          ? `Submitted: ${
                              entry.submittedAt &&
                              dayjs(entry.submittedAt).isValid()
                                ? formatDate(entry.submittedAt, language)
                                : "N/A"
                            }`
                          : isCompleted
                            ? `${
                                getLocalizedMessage(
                                  language || "en",
                                  "completedOn"
                                ) || "Completed on"
                              } ${formatDate(
                                entry._calculated?.completionDate,
                                language
                              )}`
                            : `${translatedStatus} ${
                                entry._calculated?.lastUpdatedAt
                                  ? `${
                                      getLocalizedMessage(
                                        language || "en",
                                        "asOf"
                                      ) || "as of"
                                    } ${formatDateTime(
                                      entry._calculated?.lastUpdatedAt,
                                      language
                                    )}`
                                  : ""
                              }${
                                entry._calculated?.lastUpdatedBy
                                  ? ` ${
                                      getLocalizedMessage(
                                        language || "en",
                                        "by"
                                      ) || "by"
                                    } ${entry._calculated?.lastUpdatedBy}`
                                  : ""
                              }`,
                      },
                    ],
                  ]);

                  const orderedColumnProps =
                    tableHeaderEntriesWithVisibility.map((header) => {
                      const fieldName = String(header.fieldName);

                      if (fieldName.startsWith("customField-")) {
                        const customFieldDefinition =
                          customFieldDefinitionByFieldName.get(fieldName);

                        const rawValue =
                          customFieldValueByFieldName.get(fieldName) ??
                          customFieldValueByLabel.get(
                            String(header.label ?? "")
                              .trim()
                              .toLowerCase()
                          );

                        const baseState = {
                          isHidden: header.isHidden,
                          isVisible: header.isVisible,
                        };

                        if (customFieldDefinition?.type === "Date") {
                          return {
                            component: DateCell,
                            date: rawValue
                              ? formatDate(String(rawValue))
                              : "N/A",
                            cardProps: {
                              sx: {
                                minWidth: 200,
                                width: 200,
                                maxWidth: 200,
                                overflow: "hidden",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                              },
                            },
                            ...baseState,
                          };
                        }

                        if (customFieldDefinition?.type === "Select") {
                          const selectedLabel = getDropdownLabel(
                            customFieldDefinition.options,
                            String(rawValue ?? "")
                          );
                          const optionColor = getDropdownColorValue(
                            customFieldDefinition.options,
                            String(rawValue ?? ""),
                            "#E1F1E7"
                          );

                          return {
                            component: ChipCell,
                            body: selectedLabel,
                            cardProps: {
                              sx: {
                                minWidth: 200,
                                width: 200,
                                maxWidth: 200,
                                overflow: "visible",
                                paddingLeft: "4px",
                                paddingRight: "4px",
                              },
                            },
                            chipProps: {
                              sx: {
                                backgroundColor: optionColor,
                                color: getContrastColor(
                                  optionColor,
                                  theme.palette.text.primary
                                ),
                                px: 1.5,
                                borderRadius: "8px",
                                width: "fit-content",
                                maxWidth: "calc(100% - 2px)",
                                minWidth: 0,
                                overflow: "hidden",
                                display: "inline-block",
                                boxSizing: "border-box",
                              },
                            },
                            bodyProps: {
                              sx: {
                                width: "100%",
                                minWidth: 0,
                                overflow: "hidden",
                              },
                            },
                            ...baseState,
                          };
                        }

                        return {
                          component: TextCardCell,
                          body: String(rawValue ?? "n/a"),
                          cutBodyAfter:
                            customFieldDefinition?.type === "Text"
                              ? 51
                              : undefined,
                          cardProps: {
                            sx: {
                              minWidth: 200,
                              width: 200,
                              maxWidth: 200,
                              overflow: "hidden",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            },
                          },
                          ...baseState,
                        };
                      }

                      const baseCell = baseCellByFieldName.get(fieldName);

                      return {
                        ...(baseCell ?? {
                          component: TextCardCell,
                          body: "n/a",
                        }),
                        isHidden: header.isHidden,
                        isVisible: header.isVisible,
                      };
                    }) as unknown as ProjectsTableColumnProps;

                  return {
                    columnProps: orderedColumnProps,
                    rowProps: {
                      cardProps: { sx: { cursor: "pointer" } },
                      onClick: () => {
                        const projectId = entry.id;
                        this.app?.navigate(
                          generatePath(APP_ROUTES.report, {
                            id: projectId,
                            viewname: "dashboard",
                          })
                        );
                      },
                      lockMenu: isEditLocked ?? false,
                      menuTooltip: getLockedByMessage(
                        "project",
                        language,
                        projectEditLock
                      ),
                      onMenuOpen: canUserPerformOperation({
                        userGroups: userProfile?.groups ?? [],
                        object: "project",
                        operation: "edit",
                        entityId: entry.id,
                        lowestLevelAccessRequiredToPerformThisOperation:
                          entry.userCreatedProject ? "project" : "program",
                        client: settings?.configuration?.client,
                        tool: settings?.configuration?.tool,
                      })
                        ? {
                            onEdit: async (index) => {
                              const projectId = projectItems.find(
                                (item) => projectItems.indexOf(item) === index
                              )?.id;

                              projectId != null &&
                                resourceLockPlugin?.lock(
                                  `project-${projectId}`
                                );
                              this.app?.patchAppState({
                                editForm: {
                                  currentProject: projectItems.find(
                                    (item) =>
                                      projectItems.indexOf(item) === index
                                  ),
                                },
                              });
                              projectId &&
                                this.app?.navigate(
                                  generatePath(APP_ROUTES.projectDialog, {
                                    id: projectId,
                                    isFromOverview: "true",
                                  })
                                );
                              this.app?.patchAppState({
                                overlay: await fetchOverlay(),
                                overview: await fetchOverview(),
                                editForm: {
                                  currentProject: projectItems.find(
                                    (item) =>
                                      projectItems.indexOf(item) === index
                                  ),
                                },
                              });
                            },
                            onDelete: async (index) => {
                              const projectId = projectItems.find(
                                (item) => projectItems.indexOf(item) === index
                              )?.id;

                              // show confirmation dialog and set pending project id
                              this.app?.patchAppState({
                                showDeleteProjectDialog: true,
                                pendingDeleteProjectId: projectId,
                              });
                            },
                          }
                        : undefined,
                    },
                  };
                }),
              tableHeaderEntries: tableHeaderEntriesWithVisibility,
              language,
            };
          }
        );

        projectsTableComponent.on("reorderColumns", () => {
          if (!this.app) {
            return;
          }

          const state = this.app.getAppState();

          const currentConfig = state.overviewTableConfiguration;
          this.app.patchAppState({
            overviewTableConfiguration: {
              ...currentConfig,
              tableHeaderEntries: currentConfig?.tableHeaderEntries ?? [],
              isEditMode: true,
              isDirty: true,
            },
          });
        });

        projectsTableComponent.on("addColumn", (event) => {
          if (!event?.payload || !this.app) {
            return;
          }

          const payload = event?.payload as PMTableColumnActionEventPayload;
          const state = this.app.getAppState();

          const currentConfig = state.overviewTableConfiguration;
          const nextHeaders =
            payload.headers ?? currentConfig?.tableHeaderEntries ?? [];
          this.app.patchAppState({
            overviewTableConfiguration: {
              ...currentConfig,
              tableHeaderEntries: nextHeaders,
              isEditMode: true,
              isDirty: true,
            },
          });
        });

        projectsTableComponent.on("deleteColumn", (event) => {
          if (!event?.payload || !this.app) {
            return;
          }

          const payload = event?.payload as PMTableColumnActionEventPayload;
          const state = this.app.getAppState();
          const currentConfig = state.overviewTableConfiguration;
          const newTableHeaderEntries =
            payload.headers ?? currentConfig?.tableHeaderEntries ?? [];

          this.app.patchAppState({
            overviewTableConfiguration: {
              ...currentConfig,
              tableHeaderEntries: newTableHeaderEntries,
              isEditMode: true,
              isDirty: true,
            },
          });
        });
      }
    );
  }

  /**
   * Adds every static overview card, in the exact order the 3,600-line
   * monolith did (#2222): see `overview/index.ts` for the ordered list.
   */
  buildOverview() {
    if (!this.app || !this.view) {
      return;
    }

    for (const addCard of OVERVIEW_CARDS) {
      addCard(this.view, this.app);
    }
  }
}
